<template>
  <div class="fts-settings-page" @input.capture="touched = true" @change.capture="touched = true">
    <header class="fts-settings-head">
      <div>
        <h1>General</h1>
        <p>What this workspace is called, how it looks, and the company behind it.</p>
      </div>
    </header>

    <div class="fts-settings-loading" v-if="loading">
      <fts-spinner :size="16" />
      <span>Loading&hellip;</span>
    </div>

    <section class="fts-settings-card" v-else-if="unavailable">
      <h2>{{ unavailable.title }}</h2>
      <p class="fts-settings-card-sub">{{ unavailable.body }}</p>
    </section>

    <template v-else>
      <section class="fts-settings-card">
        <div>
          <h2>Workspace</h2>
          <p class="fts-settings-card-sub">The name people see, and how it is introduced.</p>
        </div>

        <!-- In a grid cell like every other field: loose in a flex column this component
             reserves about 490px for a single line of text. -->
        <div class="fts-settings-grid-2">
          <fts-form-element
            :item="nameField"
            :form-data="formData"
            :validator="$v.formData"
            comfort-design
          />
        </div>

        <div class="fts-settings-field">
          <label class="fts-settings-field-label" for="fts-settings-description">Description</label>
          <textarea
            id="fts-settings-description"
            class="fts-settings-textarea is-short"
            v-model="formData.description"
            :maxlength="descriptionMax"
            placeholder="What this workspace is for."
          ></textarea>
          <div class="fts-settings-textarea-meta">
            <span>{{ descriptionLeft }}</span>
          </div>
        </div>
      </section>

      <section class="fts-settings-card">
        <div>
          <h2>Look</h2>
          <p class="fts-settings-card-sub">{{ lookIntro }}</p>
        </div>

        <div class="fts-settings-brand">
          <!-- W-23: icon and colours are everyone's at GA; the logo stays staff-set. -->
          <div class="fts-settings-brand-item" v-if="isInternal">
            <span class="fts-settings-field-label">Logo</span>
            <fts-image-cropper
              v-model="formData.logo"
              :upload-form-data="{ workspaceId: workspaceId }"
              :cropper-options="{ aspectRatio: null }"
              :format-file-path="formatFilePath"
            />
          </div>
          <div class="fts-settings-brand-item">
            <span class="fts-settings-field-label">Icon</span>
            <fts-image-cropper
              v-model="formData.icon"
              :upload-form-data="{ workspaceId: workspaceId }"
              :cropper-options="{ aspectRatio: 1 }"
              :format-file-path="formatFilePath"
            />
          </div>
          <div class="fts-settings-brand-item" v-for="swatch in swatches" :key="swatch.key">
            <label class="fts-settings-field-label" :for="'fts-settings-' + swatch.key">
              {{ swatch.label }}
            </label>
            <div class="fts-settings-colour">
              <input
                type="color"
                class="fts-settings-colour-dot"
                :id="'fts-settings-' + swatch.key"
                :value="swatch.value"
                @input="setColour(swatch.key, $event.target.value)"
              />
              <input
                type="text"
                class="fts-settings-input"
                :value="swatch.value"
                spellcheck="false"
                @input="setColour(swatch.key, $event.target.value)"
              />
            </div>
          </div>
        </div>
      </section>

      <section class="fts-settings-card">
        <div>
          <h2>Company details</h2>
          <p class="fts-settings-card-sub">
            Used on documents, invoices and anything this workspace sends out.
          </p>
        </div>

        <div class="fts-settings-brand-item">
          <span class="fts-settings-field-label">Company logo</span>
          <fts-image-cropper
            v-model="formData.companyLogo"
            :upload-form-data="{ workspaceId: workspaceId }"
            :cropper-options="{ aspectRatio: null }"
            :format-file-path="formatFilePath"
          />
        </div>

        <div class="fts-settings-grid-2">
          <fts-form-element
            v-for="field in companyFields"
            :key="field.fieldName"
            :item="field"
            :form-data="formData"
            :validator="$v.formData"
            comfort-design
          />
        </div>
      </section>

      <!-- Channels: the two outbound accounts this workspace sends through. Staff-only, exactly as
           they were on the old Workspace tab — nothing is opened up by moving them here. -->
      <template v-if="isInternal">
        <section class="fts-settings-card" v-for="channel in channels" :key="channel.key">
          <div>
            <h2>{{ channel.title }}</h2>
            <p class="fts-settings-card-sub">{{ channel.sub }}</p>
          </div>

          <div class="fts-settings-banner" v-if="channel.secretSet">
            <font-awesome-icon :icon="['fas', 'lock']" />
            <span>{{ channel.secretLabel }} is set. Leave it blank to keep it.</span>
          </div>

          <div class="fts-settings-grid-2">
            <fts-form-element
              v-for="field in channel.fields"
              :key="field.fieldName"
              :item="field"
              :form-data="formData"
              :validator="$v.formData"
              comfort-design
            />
          </div>
        </section>
      </template>

      <section class="fts-settings-card" v-if="isInternal">
        <div>
          <h2>Beta mode</h2>
          <p class="fts-settings-card-sub">
            Lets everyone in this workspace see features still in beta.
          </p>
        </div>
        <div class="fts-settings-row">
          <div class="fts-settings-row-text">
            <strong>Beta features</strong>
            <span>{{ formData.beta ? "On for this workspace" : "Off" }}</span>
          </div>
          <fts-toggle-switch v-model="formData.beta" />
        </div>
      </section>

      <div class="fts-settings-save" v-if="dirty">
        <span class="fts-settings-note">{{ saveNote }}</span>
        <div class="fts-settings-save-actions">
          <button type="button" class="fts-settings-btn" :disabled="saving" @click="discard">
            Discard
          </button>
          <button
            type="button"
            class="fts-settings-btn is-primary"
            :disabled="saving"
            @click="save"
          >
            {{ saving ? "Saving…" : "Save changes" }}
          </button>
        </div>
      </div>

      <settings-leave-workspace :workspace-id="workspaceId" @left="$emit('left', $event)" />

      <section class="fts-settings-card is-danger">
        <div>
          <h2>Danger zone</h2>
          <p class="fts-settings-card-sub">
            These affect the whole workspace and everyone in it.
          </p>
        </div>

        <div class="fts-settings-row" v-if="isInternal">
          <div class="fts-settings-row-text">
            <strong>Clone this workspace</strong>
            <span>Copies the design — modules, forms and automations — into a new workspace.</span>
          </div>
          <button type="button" class="fts-settings-btn" :disabled="busy" @click="clone">
            Clone
          </button>
        </div>

        <div class="fts-settings-row">
          <div class="fts-settings-row-text">
            <strong>Delete this workspace</strong>
            <span>
              Everything in it goes: every module, every record, every attachment. This cannot be
              undone.
            </span>
          </div>
          <button type="button" class="fts-settings-btn is-danger" :disabled="busy" @click="remove">
            Delete
          </button>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
/**
 * Settings › General (proposal §3 D-9) — the old modal's Workspace and Company tabs, plus the
 * staff-only channel accounts that lived alongside them.
 *
 * With this page in, the modal's remaining tabs are all ported: Plan & Credits, AI Teammate and
 * Access moved earlier, and the App tab is two app-store links and no settings at all. So this is
 * the page that makes retiring it possible.
 *
 * It reads and writes through the SAME store actions the modal used, so there is one way to
 * change a workspace, not two. What it must not do is undo r33617: that read now returns
 * `emailPassword` and `metaAPIToken` as NULL with `hasEmailPassword` / `hasMetaAPIToken` beside
 * them, and the write treats an empty secret as "leave it alone". The page therefore shows whether
 * a secret is set rather than its value, and never posts a blank over a stored one.
 *
 * Nothing else is newly exposed by the move. Everything that was `isInternal` on the old tab is
 * `isInternal` here, except the icon and the two colours: W-23 gives those to every workspace
 * admin once the area reaches GA. The logo stays staff-set.
 */
import commons from "../../commons/commons";
import designValidator from "../../commons/designValidator";
import webAppConfig from "../../commons/webApp.config";
import countriesList from "../../../json/countries.json";
import { authorizationStore } from "../../store/authorizationStore";
import { store as canvasDesignStore } from "../../store/canvasDesignStore";
import FTSSpinner from "../../elements/FTSSpinner";
import FTSFormElement from "../../elements/FTSFormElement";
import FTSImageCropper from "../../elements/FTSImageCropper";
import FTSToggleSwitch from "../../elements/FTSToggleSwitch";
import SettingsLeaveWorkspace from "./SettingsLeaveWorkspace";

const DESCRIPTION_MAX = 2000;

/** Long enough for a field component to finish reformatting, short enough to beat any typing. */
const SETTLE_MS = 600;

/** Null, undefined and "" all mean "nothing here"; anything else compares as text. */
function same(a, b) {
  const left = a === null || a === undefined ? "" : a;
  const right = b === null || b === undefined ? "" : b;
  return String(left) === String(right);
}

/** Fields the server owns and the form must never post back as its own. */
const READ_ONLY_KEYS = ["hasEmailPassword", "hasMetaAPIToken"];

export default {
  name: "SettingsGeneral",
  components: {
    "fts-spinner": FTSSpinner,
    "fts-form-element": FTSFormElement,
    "fts-image-cropper": FTSImageCropper,
    "fts-toggle-switch": FTSToggleSwitch,
    "settings-leave-workspace": SettingsLeaveWorkspace,
  },
  props: {
    workspaceId: [String, Number],
  },
  data() {
    return {
      loading: true,
      saving: false,
      busy: false,
      unavailable: null,
      formData: {},
      originalData: {},
      touched: false,
      descriptionMax: DESCRIPTION_MAX,
    };
  },
  validations() {
    return {
      formData: commons.getFormValidators(
        [this.nameField].concat(this.companyFields).concat(this.channelFields)
      ),
    };
  },
  computed: {
    isInternal() {
      return authorizationStore.getters.isInternal;
    },
    lookIntro() {
      return this.isInternal
        ? "The logo on shared pages and emails, the icon in the app, and the two colours everything else is built from."
        : "The icon in the app, and the two colours everything else is built from.";
    },
    nameField() {
      return {
        type: 0,
        fieldName: "name",
        displayName: "Workspace Name",
        required: true,
        validator: { format: designValidator.workspaceNameFormat },
      };
    },
    companyFields() {
      return [
        { type: 0, fieldName: "companyName" },
        { type: 0, fieldName: "addressLine1", displayName: "Address Line 1" },
        { type: 0, fieldName: "addressLine2", displayName: "Address Line 2" },
        { type: 0, fieldName: "addressLine3", displayName: "Address Line 3" },
        { type: 0, fieldName: "postcode" },
        { type: 0, fieldName: "state" },
        { type: 27, fieldName: "country", optionList: countriesList, optionColor: false },
        {
          type: 0,
          fieldName: "registrationNumber",
          displayName: "Business/Company Registration Number",
        },
        { type: 25, fieldName: "email" },
        { type: 26, fieldName: "telephoneNumber", country: this.formData && this.formData.country },
      ];
    },
    emailFields() {
      return [
        { type: 0, fieldName: "emailUser" },
        { input: "password", fieldName: "emailPassword" },
        { type: 0, fieldName: "emailSMTPServer", displayName: "Email SMTP Server" },
        { type: 0, fieldName: "emailSender" },
        { type: 0, fieldName: "emailSenderName" },
        { type: 2, fieldName: "emailPortNumber" },
        { type: 1, fieldName: "emailEnableSsl", displayName: "Email Enable SSL" },
      ];
    },
    whatsappFields() {
      return [
        { type: 0, fieldName: "metaAPIToken", displayName: "Meta API Token" },
        { type: 0, fieldName: "metaAccount" },
      ];
    },
    /** Only validated for staff, because only staff can see or set them. */
    channelFields() {
      return this.isInternal ? this.emailFields.concat(this.whatsappFields) : [];
    },
    channels() {
      if (!this.isInternal) return [];
      return [
        {
          key: "email",
          title: "Email",
          sub: "The SMTP account this workspace sends mail through.",
          fields: this.emailFields,
          secretSet: !!this.formData.hasEmailPassword,
          secretLabel: "The password",
        },
        {
          key: "whatsapp",
          title: "WhatsApp",
          sub: "The Meta account this workspace sends WhatsApp messages through.",
          fields: this.whatsappFields,
          secretSet: !!this.formData.hasMetaAPIToken,
          secretLabel: "The API token",
        },
      ];
    },
    swatches() {
      return [
        { key: "color1", label: "Primary colour", value: this.formData.color1 || "#2b9ec6" },
        { key: "color2", label: "Secondary colour", value: this.formData.color2 || "#1e6f8b" },
      ];
    },
    descriptionLeft() {
      const used = (this.formData.description || "").length;
      return (DESCRIPTION_MAX - used).toLocaleString("en-US") + " characters left";
    },
    /**
     * Compared field by field, with empty treated as empty.
     *
     * A plain JSON comparison said the page was dirty the moment it loaded: the field components
     * fill in what the server left out — an absent address line becomes "", the phone field
     * writes its country prefix — so the object is no longer byte-identical to the response
     * before anyone has typed anything. Offering to save a page nobody has touched trains people
     * to ignore the button.
     */
    dirty() {
      const keys = Object.keys(Object.assign({}, this.originalData, this.formData));
      return keys.some(key => !same(this.originalData[key], this.formData[key]));
    },
    workspaceName() {
      return String(this.formData.name || "Workspace").trim();
    },
    saveNote() {
      return this.isInternal && (this.formData.hasEmailPassword || this.formData.hasMetaAPIToken)
        ? "Secrets you leave blank stay as they are."
        : "";
    },
  },
  methods: {
    setColour(key, value) {
      this.$set(this.formData, key, value);
    },
    /** Same rule the modal used, so an image saved there still resolves here. */
    formatFilePath(filePath) {
      if (!filePath) return commons.defaultImage;
      const url = String(filePath).split("/");
      const filename = encodeURIComponent(url.pop());
      const formatted = `${url.join("/")}/${filename}`;
      if (formatted.toLowerCase().startsWith("/uploads")) {
        return `${webAppConfig.baseURL}/${formatted}`;
      }
      const encoded = `/api/d/${this.workspaceId}/s${formatted}`;
      return webAppConfig.baseURL ? `${webAppConfig.baseURL}${encoded}` : encoded;
    },
    async load() {
      this.loading = true;
      this.unavailable = null;
      try {
        const res = await canvasDesignStore.dispatch(
          "retrieveWorkspaceInformation",
          this.workspaceId
        );
        this.formData = Object.assign({}, res.data || {});
        this.originalData = Object.assign({}, this.formData);
        // Some field components REFORMAT what they are given -- the phone field turns
        // "+60 4 555 1234" into "+6045551234" shortly after it mounts -- so a baseline taken from
        // the server's bytes leaves the page dirty before anyone has touched it, and a "Save
        // changes" bar that is always there is one people learn to ignore. The baseline is
        // therefore taken again once the form has settled, and only while nobody has typed:
        // after that, every difference is somebody's.
        this.touched = false;
        window.setTimeout(() => {
          if (!this.touched) this.originalData = Object.assign({}, this.formData);
        }, SETTLE_MS);
      } catch (err) {
        const status = err && err.response && err.response.status;
        this.unavailable = status === 400 || status === 403
          ? {
              title: "Only a workspace admin can change these",
              body: "Ask a workspace admin if something here needs updating.",
            }
          : {
              title: "Workspace details are not available right now",
              body: "Please try again in a moment.",
            };
      }
      this.loading = false;
    },
    discard() {
      this.formData = Object.assign({}, this.originalData);
    },
    async save() {
      if (this.saving) return;
      this.$v.formData.$touch();
      if (this.$v.formData.$invalid) {
        this.$toasted.global.invalidFormValidate();
        return;
      }

      this.saving = true;
      try {
        const payload = Object.assign({}, this.formData, { id: this.workspaceId });
        // The server decides these; sending them back would only invite it to read its own
        // answer as an instruction.
        READ_ONLY_KEYS.forEach(key => delete payload[key]);
        await canvasDesignStore.dispatch("updateWorkspaceInformation", payload);
        // Re-read rather than trust the payload: the server masks secrets and may normalise
        // what it stored, and the form has to show what is actually there now.
        await this.load();
        this.$toasted.success("Workspace is updated successfully");
      } catch (err) {
        const data = err && err.response && err.response.data;
        this.$toasted.global.error(err, (data && data.message) || "Those changes could not be saved.");
      } finally {
        this.saving = false;
      }
    },
    async clone() {
      if (this.busy) return;
      const name = this.workspaceName;
      const confirmed = await this.$confirmation.show({
        type: "confirm",
        title: "Clone workspace",
        text: 'This copies the design of "' + name + '" into a new workspace. Records are not copied.',
      });
      if (confirmed !== true) return;

      this.busy = true;
      try {
        await canvasDesignStore.dispatch("cloneWorkspace", { id: String(this.workspaceId) });
        this.$toasted.success("Workspace cloned successfully");
      } catch (err) {
        this.$toasted.global.error(err, "That workspace could not be cloned.");
      } finally {
        this.busy = false;
      }
    },
    async remove() {
      if (this.busy) return;
      // The same type-the-name confirmation the modal used. A workspace is everything its people
      // have made and there is no undo, so an OK button on its own is too easy to hit.
      const name = this.workspaceName;
      const confirmed = await this.$confirmation.show({
        type: "delete",
        title: "Workspace",
        text: name,
        enableConfirmationKey: true,
        confirmationKey: name,
      });
      if (confirmed !== true) return;

      this.busy = true;
      try {
        await canvasDesignStore.dispatch("deleteWorkspace", { id: String(this.workspaceId) });
        this.$toasted.success("Workspace deleted successfully");
        this.$emit("deleted", this.workspaceId);
      } catch (err) {
        this.$toasted.global.error(err, "That workspace could not be deleted.");
      } finally {
        this.busy = false;
      }
    },
  },
  watch: {
    workspaceId() {
      this.formData = {};
      this.load();
    },
  },
  mounted() {
    this.load();
  },
};
</script>
